/**
 * Client-side canonical URL utilities
 * Fix for 301 loop issue - handle canonical URL redirects in browser instead of server
 */

export interface PostForCanonical {
  id: number;
  slug?: string;
  title?: {
    rendered?: string;
  } | string;
}

/**
 * Generate canonical slug from post data
 */
export const generateCanonicalSlug = (post: PostForCanonical): string => {
  // Priority 1: Use WordPress slug if valid and not just ID
  if (post.slug && post.slug !== post.id.toString() && post.slug.length > 3) {
    return post.slug;
  }
  
  // Priority 2: Generate from title if slug is missing/invalid  
  const title = typeof post.title === 'string' 
    ? post.title 
    : post.title?.rendered;
    
  if (title) {
    const cleanTitle = title.replace(/<[^>]*>/g, '').trim();
    const slug = cleanTitle.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    
    // Ensure slug is meaningful and not just the post ID
    if (slug.length >= 3 && slug !== post.id.toString()) {
      return slug;
    }
  }
  
  // Priority 3: Last resort fallback
  return `post-${post.id}`;
};

/**
 * Generate canonical URL for a post
 */
export const generateCanonicalUrl = (post: PostForCanonical): string => {
  const canonicalSlug = generateCanonicalSlug(post);
  return `/post/${post.id}/${canonicalSlug}.html`;
};

/**
 * Check if current URL is canonical and redirect if needed (client-side)
 * Returns true if redirect happened, false if URL is already canonical
 */
export const checkAndRedirectToCanonical = (
  post: PostForCanonical, 
  currentPath: string,
  navigate: (path: string, options?: any) => void,
  options: { replace?: boolean; silent?: boolean } = {}
): boolean => {
  if (!post || !currentPath) return false;
  
  const { replace = true, silent = false } = options;
  
  // Extract current post ID and slug from URL
  const currentMatch = currentPath.match(/^\/post\/(\d+)\/([^\/]+)\.html$/);
  if (!currentMatch) return false; // Not a post URL
  
  const [, currentPostId, currentSlug] = currentMatch;
  
  // Check if post ID matches
  if (parseInt(currentPostId) !== post.id) return false;
  
  // Generate canonical URL
  const canonicalUrl = generateCanonicalUrl(post);
  
  // Check if current URL is already canonical
  if (currentPath === canonicalUrl) {
    if (!silent) {
      console.log(`✅ [Canonical] URL is already canonical: ${currentPath}`);
    }
    return false; // No redirect needed
  }
  
  // Decode URL-encoded slug for comparison
  let decodedCurrentSlug = currentSlug;
  try {
    decodedCurrentSlug = decodeURIComponent(currentSlug);
  } catch (e) {
    // If decoding fails, use as-is
  }
  
  const canonicalSlug = generateCanonicalSlug(post);
  
  // Check if decoded slug matches canonical slug
  if (decodedCurrentSlug === canonicalSlug) {
    if (!silent) {
      console.log(`✅ [Canonical] URL slug matches after decoding: ${currentPath}`);
    }
    return false; // No redirect needed
  }
  
  // Perform client-side redirect to canonical URL
  console.log(`🔄 [Canonical] Client redirect: ${currentPath} → ${canonicalUrl}`);
  
  try {
    navigate(canonicalUrl, { replace });
    return true; // Redirect performed
  } catch (error) {
    console.error('❌ [Canonical] Redirect failed:', error);
    return false;
  }
};

/**
 * Extract post ID from URL path
 */
export const extractPostIdFromPath = (path: string): number | null => {
  const match = path.match(/^\/post\/(\d+)\//);
  return match ? parseInt(match[1]) : null;
};

/**
 * Check if URL is a post URL
 */
export const isPostUrl = (path: string): boolean => {
  return /^\/post\/\d+\/[^\/]+\.html$/.test(path);
};

/**
 * Update browser history with canonical URL without triggering navigation
 */
export const updateHistoryWithCanonical = (post: PostForCanonical): void => {
  if (typeof window === 'undefined') return;
  
  const canonicalUrl = generateCanonicalUrl(post);
  const currentPath = window.location.pathname;
  
  if (currentPath !== canonicalUrl && isPostUrl(currentPath)) {
    try {
      window.history.replaceState(null, '', canonicalUrl);
      console.log(`🔗 [Canonical] History updated: ${canonicalUrl}`);
    } catch (error) {
      console.error('❌ [Canonical] History update failed:', error);
    }
  }
}; 